/* =====================================================================
 * CAD Agent API client — `/api/v1/cad/*` 의 thin wrapper.
 *
 * 프로젝트 / 폴더 / 파일 영속화 엔드포인트를 React Query 가 쉽게 쓸 수
 * 있도록 함수 단위로 노출한다. 파일 자체는 `workspace_files` 테이블을
 * 재사용하므로 응답 모양은 그대로 `WorkspaceFile` 이다 — 파일 저장소
 * 페이지의 동기화를 자연스럽게 한다.
 *
 * 멀티테넌트
 *   본 클라이언트는 CAD 가 활성화된 테넌트(B2C local / dev / prod) 에서만
 *   호출된다. Samsung onprem 매니페스트엔 CAD 가 없으므로 호출 진입
 *   자체가 없다.
 * ===================================================================== */

import type { WorkspaceFile } from '@auw/workspace-utils';
import { authFetch, getHeaders as baseGetHeaders, API_BASE_URL } from './authFetch';

const CAD_PREFIX = `${API_BASE_URL}/api/v1/cad`;

function jsonHeaders(): Record<string, string> {
  return baseGetHeaders(true);
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface CadProject {
  project_id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CadFolder {
  folder_id: string;
  project_id: string;
  parent_folder_id: string | null;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CadUploadResponse {
  uploaded: WorkspaceFile[];
  count: number;
  errors: Array<{ file: string; error: string }> | null;
}

/* ------------------------------------------------------------------ */
/* Projects                                                            */
/* ------------------------------------------------------------------ */

export async function listCadProjects(): Promise<CadProject[]> {
  const res = await authFetch(`${CAD_PREFIX}/projects`, {
    headers: jsonHeaders(),
  });
  const data = await unwrap<{ projects: CadProject[]; count: number }>(res);
  return data.projects;
}

export async function createCadProject(name: string): Promise<CadProject> {
  const res = await authFetch(`${CAD_PREFIX}/projects`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return unwrap<CadProject>(res);
}

export async function renameCadProject(
  projectId: string,
  name: string,
): Promise<CadProject> {
  const res = await authFetch(`${CAD_PREFIX}/projects/${projectId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return unwrap<CadProject>(res);
}

export async function deleteCadProject(projectId: string): Promise<void> {
  const res = await authFetch(`${CAD_PREFIX}/projects/${projectId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ status: string; project_id: string }>(res);
}

/* ------------------------------------------------------------------ */
/* Folders                                                             */
/* ------------------------------------------------------------------ */

export async function listCadFolders(projectId: string): Promise<CadFolder[]> {
  const res = await authFetch(`${CAD_PREFIX}/projects/${projectId}/folders`, {
    headers: jsonHeaders(),
  });
  const data = await unwrap<{ folders: CadFolder[]; count: number }>(res);
  return data.folders;
}

export async function createCadFolder(
  projectId: string,
  name: string,
  parentFolderId: string | null = null,
): Promise<CadFolder> {
  const res = await authFetch(`${CAD_PREFIX}/projects/${projectId}/folders`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      name,
      parent_folder_id: parentFolderId,
    }),
  });
  return unwrap<CadFolder>(res);
}

export async function renameCadFolder(
  folderId: string,
  name: string,
): Promise<CadFolder> {
  const res = await authFetch(`${CAD_PREFIX}/folders/${folderId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return unwrap<CadFolder>(res);
}

export async function deleteCadFolder(folderId: string): Promise<void> {
  const res = await authFetch(`${CAD_PREFIX}/folders/${folderId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  await unwrap<{ status: string; folder_id: string }>(res);
}

/* ------------------------------------------------------------------ */
/* Files                                                               */
/* ------------------------------------------------------------------ */

/** `unclassified` 는 metadata 에 status 키 자체가 없는 파일 (비분류 대상). */
export type CadDocClassificationStatusFilter =
  | 'pending'
  | 'completed'
  | 'failed'
  | 'unclassified';

export interface CadFileFilters {
  /** 점 유무 무관, 대소문자 무관. 빈 배열/undefined = 필터 없음. */
  readonly extensions?: readonly string[];
  /** `cad_doc_category` enum. */
  readonly categories?: readonly string[];
  /** `cad_doc_subtype` enum (categories 와 AND). */
  readonly subtypes?: readonly string[];
  /** 분류 상태 — `unclassified` 포함. */
  readonly classificationStatuses?: readonly CadDocClassificationStatusFilter[];
}

function appendCsv(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined,
): void {
  if (!values || values.length === 0) return;
  const joined = values.map((v) => v.trim()).filter(Boolean).join(',');
  if (joined) params.set(key, joined);
}

export async function listCadFiles(
  projectId: string,
  folderId: string | null,
  filters?: CadFileFilters,
  opts?: { all?: boolean },
): Promise<WorkspaceFile[]> {
  const params = new URLSearchParams();
  // all=true 면 폴더 깊이와 무관하게 프로젝트 전체 파일을 가져온다(트리 hydrate).
  // 깊은 폴더에 올린 파일이 새로고침/재방문 후에도 트리에 유지되도록 한다.
  if (opts?.all) {
    params.set('all', 'true');
  } else if (folderId) {
    params.set('folder_id', folderId);
  }
  if (filters) {
    appendCsv(params, 'extensions', filters.extensions);
    appendCsv(params, 'categories', filters.categories);
    appendCsv(params, 'subtypes', filters.subtypes);
    appendCsv(params, 'classification_statuses', filters.classificationStatuses);
  }
  const qs = params.toString();
  const res = await authFetch(
    `${CAD_PREFIX}/projects/${projectId}/files${qs ? `?${qs}` : ''}`,
    { headers: jsonHeaders() },
  );
  const data = await unwrap<{ files: WorkspaceFile[]; count: number }>(res);
  return data.files;
}

/** 서버에 영속된 CAD 파일 썸네일(WebP)을 file_id 로 받아 dataURL 로 반환.
 *  어떤 기기/브라우저에서도 동일 썸네일을 받는다(서버 영속). 없거나 비대상이면 null. */
export async function fetchCadThumbnail(fileId: string): Promise<string | null> {
  try {
    const res = await authFetch(`${CAD_PREFIX}/files/${fileId}/thumbnail`, {
      headers: { Accept: 'image/webp,image/*' },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function uploadCadFiles(
  projectId: string,
  files: ReadonlyArray<{ blob: Blob; name: string }>,
  folderId: string | null,
): Promise<CadUploadResponse> {
  const form = new FormData();
  for (const f of files) {
    form.append('files', f.blob, f.name);
  }
  if (folderId) form.append('folder_id', folderId);

  const res = await authFetch(`${CAD_PREFIX}/projects/${projectId}/files`, {
    method: 'POST',
    body: form,
  });
  return unwrap<CadUploadResponse>(res);
}

export async function moveCadFile(
  fileId: string,
  folderId: string | null,
): Promise<WorkspaceFile> {
  const res = await authFetch(`${CAD_PREFIX}/files/${fileId}/move`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ folder_id: folderId }),
  });
  return unwrap<WorkspaceFile>(res);
}

/* ------------------------------------------------------------------ */
/* LLM (분류) — `/cad/llm/*`                                            */
/* ------------------------------------------------------------------ */

export interface CadClassifyResponse {
  queued: number;
  skipped: Array<{ file_id: string; reason: string }>;
}

export interface CadDocCategoryEntry {
  category: string;
  label: string;
}

export interface CadDrawingSubtypeEntry {
  subtype: string;
  label: string;
}

export interface CadDocCategoriesResponse {
  categories: CadDocCategoryEntry[];
  drawing_subtypes: CadDrawingSubtypeEntry[];
}

export type CadExtractionFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array_string'
  | 'array_object';

export interface CadExtractionField {
  key: string;
  label: string;
  description: string;
  type: CadExtractionFieldType;
  shape?: CadExtractionField[];
  example?: string | null;
}

export interface CadExtractionGroup {
  key: string;
  label: string;
  fields: CadExtractionField[];
}

export interface CadExtractionDefaultsResponse {
  category: string;
  subtype: string | null;
  tool: string;
  schema_version: string;
  groups: CadExtractionGroup[];
}

export interface CadExtractionResponse {
  queued: number;
  skipped: Array<{ file_id: string; reason: string }>;
}

export interface CadExtractedValueEdit {
  path: Array<string | number>;
  value: unknown;
}

export interface CadClassificationUpdate {
  category: string;
  subtype?: string | null;
}

/**
 * 업로드된 CAD 파일 1건/배치를 비동기로 분류 트리거. 응답은 큐잉 직후 즉시
 * 반환되며, 결과는 `workspace_files.metadata.cad_doc_*` 에 비동기로 기록된다.
 * 프론트는 listCadFiles 폴링으로 `cad_doc_classification_status` 를 확인.
 */
export async function classifyCadFiles(
  fileIds: ReadonlyArray<string>,
): Promise<CadClassifyResponse> {
  if (fileIds.length === 0) {
    return { queued: 0, skipped: [] };
  }
  const res = await authFetch(`${CAD_PREFIX}/llm/classify`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ file_ids: fileIds }),
  });
  return unwrap<CadClassifyResponse>(res);
}

/** AI 자동분류 오분류를 사용자가 수동 보정. */
export async function updateCadFileClassification(
  fileId: string,
  payload: CadClassificationUpdate,
): Promise<WorkspaceFile> {
  const res = await authFetch(`${CAD_PREFIX}/files/${fileId}/classification`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  return unwrap<WorkspaceFile>(res);
}

/** 분류 카테고리 enum + 도면 subtype 한국어 라벨 (UI chip 동기화용). */
export async function listCadDocCategories(): Promise<CadDocCategoriesResponse> {
  const res = await authFetch(`${CAD_PREFIX}/llm/classify/categories`, {
    headers: jsonHeaders(),
  });
  return unwrap<CadDocCategoriesResponse>(res);
}

export async function getCadExtractionDefaults(
  category: string,
  subtype?: string | null,
): Promise<CadExtractionDefaultsResponse> {
  const params = new URLSearchParams({ category });
  if (subtype) params.set('subtype', subtype);
  const res = await authFetch(`${CAD_PREFIX}/llm/extract/defaults?${params.toString()}`, {
    headers: jsonHeaders(),
  });
  return unwrap<CadExtractionDefaultsResponse>(res);
}

export async function triggerCadExtraction(
  fileIds: ReadonlyArray<string>,
  overlay?: Record<string, unknown> | null,
): Promise<CadExtractionResponse> {
  if (fileIds.length === 0) {
    return { queued: 0, skipped: [] };
  }
  const res = await authFetch(`${CAD_PREFIX}/llm/extract`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ file_ids: fileIds, overlay: overlay ?? undefined }),
  });
  return unwrap<CadExtractionResponse>(res);
}

export async function patchCadExtractedValues(
  fileId: string,
  edits: ReadonlyArray<CadExtractedValueEdit>,
  pageNo?: number | null,
  regionType?: string | null,
  regionIndex?: number | null,
): Promise<WorkspaceFile> {
  const body: Record<string, unknown> = { edits };
  if (pageNo != null) body.page_no = pageNo;
  if (regionType != null) body.region_type = regionType;
  if (regionIndex != null) body.region_index = regionIndex;
  const res = await authFetch(`${CAD_PREFIX}/files/${fileId}/extracted-values`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  return unwrap<WorkspaceFile>(res);
}

/* ------------------------------------------------------------------ */
/* Region-first 추출 결과 type                                          */
/* ------------------------------------------------------------------ */

export interface CadExtractedRegion {
  type: string;
  label: string;
  /** [x1, y1, x2, y2] normalized 0~1, 페이지 width/height 비율. */
  bbox: [number, number, number, number] | null;
  region_index: number;
  /** view 인 경우만: Pass1 에서 전체 layout 컨텍스트로 결정한 view 종류
   *  (front/top/side/section/detail/...). */
  view_type?: string | null;
  /** view 인 경우만: 도면에 적힌 원문 라벨 (예: "SECTION B-B", "DETAIL A"). */
  view_label?: string | null;
  values: Record<string, unknown>;
  schema_version?: string;
  tool?: string;
  model?: string;
  crop_meta?: Record<string, unknown>;
  /** AI 가 그 region 의 sub-schema 안에서 실제로 값을 채운 leaf 개수.
   *  0 이면 빈 region (빈 영역을 view 로 잘못 detect 한 경우 등). */
  populated_count?: number;
  status?: 'completed' | 'failed';
  error?: string | null;
  /** AI 가 schema 와 별개로 합성한 region 의미 요약 (plain text, 1~4문장).
   *  유사 도면 검색 임베딩 source 이자 frontend description banner 의 본문. */
  description?: string;
}

export interface CadExtractedPage {
  page_no: number;
  regions: CadExtractedRegion[];
  status?: 'completed' | 'partial' | 'failed';
  error?: string | null;
}

/* ------------------------------------------------------------------ */
/* Page raster (CAD 상세 미리보기 + bbox overlay 좌표계)               */
/* ------------------------------------------------------------------ */

export interface CadPageMeta {
  /** 1-based 페이지 번호. */
  page_no: number;
  /** raster 픽셀 width (LLM bbox 좌표계 기준). */
  width: number;
  /** raster 픽셀 height. */
  height: number;
}

export interface CadPagesResponse {
  raster_dpi: number;
  pages: CadPageMeta[];
}

export async function listCadFilePages(
  fileId: string,
  dpi?: number,
): Promise<CadPagesResponse> {
  const params = new URLSearchParams();
  if (dpi) params.set('dpi', String(dpi));
  const qs = params.toString();
  const res = await authFetch(
    `${CAD_PREFIX}/files/${fileId}/pages${qs ? `?${qs}` : ''}`,
    { headers: jsonHeaders() },
  );
  return unwrap<CadPagesResponse>(res);
}

/**
 * 단일 페이지 raster 를 Blob 으로 받아 `URL.createObjectURL` 로 박을 수 있는
 * object URL 을 반환한다.
 *
 * 인증: 본 백엔드 라우터는 ``get_validated_user_id`` 의존성을 통과해야 한다
 * (Authorization Bearer 헤더). `<img src="...">` 는 Authorization 헤더를 못
 * 보내기 때문에 authFetch 로 직접 GET → Blob → object URL 로 변환해야
 * 안정적이다.
 *
 * **caller 책임**: 컴포넌트 unmount 또는 page 전환 시 `URL.revokeObjectURL`
 * 로 메모리 해제할 것.
 */
export async function fetchCadFilePageImageBlobUrl(
  fileId: string,
  pageNo: number,
  dpi?: number,
): Promise<{ blobUrl: string; width: number; height: number }> {
  const params = new URLSearchParams();
  if (dpi) params.set('dpi', String(dpi));
  const qs = params.toString();
  const res = await authFetch(
    `${CAD_PREFIX}/files/${fileId}/pages/${pageNo}/image${qs ? `?${qs}` : ''}`,
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  const width = Number(res.headers.get('X-Cad-Page-Width')) || 0;
  const height = Number(res.headers.get('X-Cad-Page-Height')) || 0;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  return { blobUrl, width, height };
}

/* ------------------------------------------------------------------ */
/* Similar drawings (pgvector recall + atom transport rerank)            */
/* ------------------------------------------------------------------ */

/**
 * 유사 도면 한 건. backend `SimilarDrawingMatch` 와 1:1 매칭.
 *
 * `score` 는 0~1 사이의 schema-invariant atom transport 기반 calibrated
 * 유사도다. raw cosine 이 아니며, `score_breakdown` 에 transport distance /
 * coverage / atom counts 가 함께 온다.
 */
export interface CadSimilarDrawingMatch {
  file_id: string;
  original_name: string;
  score: number;
  score_label?: string;
  score_breakdown?: Record<string, number>;
  description: string;
  cad_doc_category: string | null;
  cad_doc_subtype: string | null;
  cad_doc_subtype_label: string | null;
  page_count: number | null;
  thumbnail_url: string | null;
}

export interface CadSimilarDrawingsResponse {
  matches: CadSimilarDrawingMatch[];
}

/**
 * 본인이 보유한 다른 도면 중 `fileId` 와 가장 유사한 `topK` 개를 반환.
 *
 * 가드:
 *   - 입력 file 이 본인 소유 CAD 도면이어야 함
 *   - cad_embedding_status === 'completed' 이어야 함 (그 외 상태는 422)
 *
 * 실패 케이스 (HTTP 4xx) 는 `Error` 메시지에 detail 이 그대로 담겨
 * frontend SimilarDrawingsPanel 이 사용자 친화 메시지로 매핑할 수 있다.
 */
export async function getCadSimilarDrawings(
  fileId: string,
  topK = 10,
): Promise<CadSimilarDrawingMatch[]> {
  const res = await authFetch(`${CAD_PREFIX}/llm/similar-drawings`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ file_id: fileId, top_k: topK }),
  });
  const data = await unwrap<CadSimilarDrawingsResponse>(res);
  return data.matches;
}

export interface CadInventoryCheckItem {
  original_bom_index: number;
  bom_item?: Record<string, unknown>;
  part_number?: string | null;
  part_name?: string | null;
  description?: string | null;
  material?: string | null;
  status: '재고 있음' | '수량 부족' | '재고 없음' | '확인 불가' | string;
  required_quantity?: number | string | null;
  available_quantity?: number | string | null;
  unit?: string | null;
  matched_part_number?: string | null;
  matched_part_name?: string | null;
  matched_material_description?: string | null;
  source_project_id?: string | null;
  source_file_id?: string | null;
  source_file?: string | null;
  source_sheet?: string | null;
  source_range?: string | null;
  evidence?: string;
  confidence?: number;
}

export interface CadInventoryCheckResponse {
  status: string;
  items: CadInventoryCheckItem[];
  summary?: string;
  sources?: Array<Record<string, unknown>>;
  raw_text?: string | null;
}

export async function checkCadInventory(
  fileId: string,
  userLanguage?: string,
): Promise<CadInventoryCheckResponse> {
  const res = await authFetch(`${CAD_PREFIX}/llm/inventory-check`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      file_id: fileId,
      user_language: userLanguage,
    }),
  });
  return unwrap<CadInventoryCheckResponse>(res);
}
